import { Component, ElementRef, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Select } from '@ngxs/store';
import { AdminPermissions } from 'app/library/feature/enums/admin-permissions.enum';
import { FinalGradeState } from 'app/library/final-grade/models/final-grade.state';
import { ModuleUserExtended } from 'app/library/module-user/models/module-user.extended.model';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { fuseAnimations } from 'app/projects/fuse/src/lib/animations';
import { FeatureSwitchName } from 'app/projects/shared/src/lib/enums/feature-switch.enum';
import { FeatureSwitchPipe } from 'app/projects/shared/src/lib/pipes/feature-switch.pipe';
import { User } from 'app/projects/user/src/lib/models/user';
import { UserState } from 'app/projects/user/src/lib/models/user.state';
import { Observable } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { Navigation } from 'swiper/modules';
import { SwiperOptions } from 'swiper/types/swiper-options';
import { TenantDataService } from '../../../../config/services/tenant-data.service';
import { Feature } from '../../../feature/models/feature.model';
import { FeatureState } from '../../../feature/models/feature.state';
import { FinalGrade } from '../../../final-grade/models/final-grade.model';
import { TagMultiSelectService } from '../../../tag/services/multi-select.service';

@Component({
    selector: 'app-my-student-modules-list',
    templateUrl: './my-student-modules-list.component.html',
    styleUrls: ['./my-student-modules-list.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations,
})
export class MyStudentModulesListComponent extends UnsubscribeOnDestroy implements OnInit {
    @Select(FeatureState.getEntities)
    feature$: Observable<Feature[]>;
    features: Feature[];

    @Select(UserState.getMyInfo)
    profile$: Observable<User>;
    profile: User;

    @Select(FinalGradeState.getEntities)
    _finalGrade$: Observable<FinalGrade[]>;
    finalGrades: {
        [key: string]: FinalGrade;
    };

    @Input()
    moduleUsers: ModuleUserExtended[];

    isCertificateOfAttendanceFeatureEnabled: boolean;

    @ViewChild('swiperEl', { static: false }) swiperEl!: ElementRef;

    public swiperConfig: SwiperOptions = {
        modules: [Navigation],
        a11y: { enabled: true },
        direction: 'horizontal',
        slidesPerView: 'auto',
        spaceBetween: 8,
        keyboard: false,
        mousewheel: false,
        scrollbar: false,
        pagination: false,
        allowTouchMove: false,
        updateOnWindowResize: true,
        slidesOffsetBefore: 80,
        slidesOffsetAfter: 80,
        // Responsive breakpoints
        breakpoints: {
            // when window width is >= 1280px
            740: {
                slidesPerView: 2,
            },
            1280: {
                slidesPerView: 3,
            },
            1680: {
                slidesPerView: 4,
            },
        },
        navigation: {
            prevEl: '.student-swiper-button-prev',
            nextEl: '.student-swiper-button-next',
        },
    };

    constructor(private _tenantDataService: TenantDataService, private _featureSwitchPipe: FeatureSwitchPipe, public tagMultiSelectService: TagMultiSelectService) {
        super();

        this.isCertificateOfAttendanceFeatureEnabled = this._featureSwitchPipe.transform(FeatureSwitchName.CertificateOfAttendance);
    }

    ngOnInit(): void {
        this.profile$
            .pipe(
                filter((profile) => !!profile),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((profile) => {
                this.profile = profile;
            });

        this._finalGrade$.pipe(takeUntil(this._unsubscribeAll)).subscribe((finalGrades) => {
            const temp = {};

            if (finalGrades) {
                finalGrades.forEach((finalGrade) => (temp[finalGrade.moduleId] = finalGrade));
            }

            this.finalGrades = temp;
        });
    }

    ngAfterViewInit(): void {
        Object.assign(this.swiperEl.nativeElement, this.swiperConfig);
        this.swiperEl.nativeElement.initialize();

        const swiperContainer = this.swiperEl.nativeElement.shadowRoot.querySelector('.swiper') as HTMLElement;
        if (swiperContainer) {
            swiperContainer.style.overflow = 'visible';
        }
    }

    private canAddNewModule(): 'yes' | 'no' | 'private' | 'basic' {
        if (
            this.features &&
            this.features.filter((feature) => feature.id === 'module_visibility' && feature.status === 'inactive').length === 1 &&
            this.features.filter((feature) => feature.id === 'e_learning' && feature.status === 'inactive').length === 1 &&
            this.profile &&
            this.profile.permissions.indexOf(AdminPermissions.TENANT_MANAGEMENT) !== -1
        ) {
            return 'basic';
        }

        if (this.profile && this.profile.permissions.indexOf(AdminPermissions.TENANT_MANAGEMENT) !== -1) {
            return 'yes';
        }

        if (
            this.features &&
            this.features.filter((feature) => feature.id === 'module_visibility' && feature.status === 'active').length === 1 &&
            this._tenantDataService.data.canCreatePrivateModule
        ) {
            return 'private';
        }

        return 'no';
    }

    get getAddNewModuleLabel(): string {
        switch (this.canAddNewModule()) {
            case 'yes':
            case 'basic':
                return 'PAGE.ALL_MODULES.BUTTON.ADD';
            case 'private':
                return 'PAGE.ALL_MODULES.BUTTON.ADD_PRIVATE';
            default:
                return null;
        }
    }

    get getAddNewModuleRoute(): any[] {
        switch (this.canAddNewModule()) {
            case 'yes':
                return ['', { outlets: { modal: 'new-module' } }];
            case 'private':
                return ['', { outlets: { modal: 'new-module/private' } }];
            case 'basic':
                return ['', { outlets: { modal: 'new-module/basic' } }];
            default:
                return null;
        }
    }
}
